/**
 * Tappable debug controls (shown only in debug mode): time warp, time skip
 * through the offline sim, and a population spawner. Lives on the left edge
 * where it can't collide with the tray or menu.
 */
export interface DebugActions {
  warp: (mult: number) => void;
  skip: (hours: number) => void;
  spawn: (count: number) => void;
  setPaused: (paused: boolean) => void;
}

export class DebugPanel {
  private activeWarpButton: HTMLButtonElement | null = null;

  constructor(parent: HTMLElement, actions: DebugActions, initialPaused = false) {
    const panel = document.createElement('div');
    panel.style.cssText = [
      'position:fixed',
      'left:4px',
      'top:38%',
      'display:flex',
      'flex-direction:column',
      'gap:4px',
      'pointer-events:auto',
      'z-index:25',
    ].join(';');

    const mkButton = (label: string, onTap: (b: HTMLButtonElement) => void): HTMLButtonElement => {
      const b = document.createElement('button');
      b.className = 'w94-btn';
      b.textContent = label;
      b.style.cssText = 'min-width:52px;height:30px;padding:0 6px;font-size:9px;pointer-events:auto;';
      b.addEventListener('click', () => onTap(b));
      panel.appendChild(b);
      return b;
    };

    const label = document.createElement('div');
    label.textContent = 'DEBUG';
    label.style.cssText =
      'font:bold 8px/1 monospace;letter-spacing:2px;color:var(--w94-terminal);background:var(--w94-terminal-bg);padding:3px 4px;text-align:center;';
    panel.appendChild(label);

    // pause freezes the sim (worker stops ticking); armed = paused.
    // persists across reloads — a paused world stays frozen in amber.
    let paused = initialPaused;
    const pauseBtn = mkButton(paused ? '▶ RESUME' : '❚❚ PAUSE', (b) => {
      paused = !paused;
      actions.setPaused(paused);
      b.textContent = paused ? '▶ RESUME' : '❚❚ PAUSE';
      b.classList.toggle('w94-armed', paused);
    });
    pauseBtn.classList.toggle('w94-armed', paused);

    const warpButton = (mult: number, text: string): HTMLButtonElement =>
      mkButton(text, (b) => {
        actions.warp(mult);
        this.activeWarpButton?.classList.remove('w94-armed');
        this.activeWarpButton = b;
        b.classList.add('w94-armed');
      });
    this.activeWarpButton = warpButton(1, 'x1');
    this.activeWarpButton.classList.add('w94-armed');
    warpButton(10, 'x10');
    warpButton(60, 'x60');
    mkButton('8H >>', () => actions.skip(8));
    mkButton('3D >>', () => actions.skip(72));
    mkButton('+10', () => actions.spawn(10));

    parent.appendChild(panel);
  }
}
